import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  ArrowLeft,
  Check,
  Plus,
  Minus,
  Star,
} from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import StickyCta from "@/components/site/StickyCta";
import { productsBySlug } from "@/data/products";

function gotoContact(navigate) {
  navigate("/#contact");
}

function Section({ id, children, className = "", bg = "" }) {
  return (
    <section id={id} className={`relative py-20 md:py-28 ${bg} ${className}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">{children}</div>
    </section>
  );
}

function Caption({ children }) {
  return <div className="caption mb-6">{children}</div>;
}

function H2({ children, className = "" }) {
  return (
    <h2
      className={`font-serif-display text-4xl sm:text-5xl md:text-6xl tracking-tight text-[#1A1A1A] leading-[1.05] ${className}`}
    >
      {children}
    </h2>
  );
}

function Pill({ children, className = "" }) {
  return (
    <span
      className={`px-3 py-1.5 rounded-full border border-[#E8E6E1] bg-[#F5F4F0] text-[12px] text-[#1A1A1A] ${className}`}
    >
      {children}
    </span>
  );
}

export default function Product() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const product = productsBySlug[slug];

  const [openFaq, setOpenFaq] = useState(null);
  const [openModule, setOpenModule] = useState(0);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  if (!product) {
    return (
      <main className="bg-[#FDFCFB] min-h-screen">
        <Nav />
        <div className="pt-40 pb-32 max-w-2xl mx-auto px-6 text-center">
          <Caption>404 — Product not found</Caption>
          <H2>This product doesn't exist (yet).</H2>
          <Link
            to="/"
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1A1A1A] text-[#FDFCFB] text-sm hover:bg-[#333]"
          >
            <ArrowLeft className="w-4 h-4" /> Back home
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  const onCta = () => gotoContact(navigate);

  return (
    <main className="bg-[#FDFCFB] text-[#1A1A1A] min-h-screen">
      <Nav />

      {/* ---------- Hero ---------- */}
      <Section id="hero" className="!pt-32 md:!pt-40 !pb-16 md:!pb-20">
        <Link
          to="/#what-we-build"
          data-testid="product-back"
          className="inline-flex items-center gap-2 text-sm text-[#6B6A68] hover:text-[#1A1A1A] mb-10 group"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          All products
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-end">
          <div className="md:col-span-7">
            <Caption>{product.hero.eyebrow}</Caption>
            {product.brand && (
              <div className="font-mono-tech text-[11px] tracking-[0.2em] uppercase text-[#1A1A1A] mb-3">
                {product.brand}
              </div>
            )}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="font-serif-display text-[40px] sm:text-6xl md:text-7xl tracking-tighter leading-[1.02] text-[#1A1A1A]"
            >
              {product.name}
            </motion.h1>
            <p className="mt-6 max-w-xl text-[#6B6A68] text-base md:text-lg leading-relaxed">
              {product.tagline}
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                data-testid={`product-${product.slug}-cta-primary`}
                onClick={onCta}
                className="group inline-flex items-center gap-2.5 px-7 py-4 rounded-full bg-[#1A1A1A] text-[#FDFCFB] text-sm font-medium shadow-[0_18px_40px_-12px_rgba(0,0,0,0.5)] hover:-translate-y-0.5 transition-all"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#7EE787] shadow-[0_0_8px_rgba(126,231,135,0.7)]" />
                Start Free
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
              <button
                data-testid={`product-${product.slug}-cta-secondary`}
                onClick={onCta}
                className="px-7 py-4 rounded-full border border-[#1A1A1A]/20 text-[#1A1A1A] text-sm hover:bg-[#1A1A1A] hover:text-[#FDFCFB] transition-all"
              >
                Book a Demo
              </button>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-[11px] tracking-[0.18em] uppercase font-mono-tech text-[#6B6A68]">
              {product.hero.trustBadges.map((b, i) => (
                <React.Fragment key={b}>
                  <span>{b}</span>
                  {i < product.hero.trustBadges.length - 1 && (
                    <span className="w-1 h-1 rounded-full bg-[#6B6A68]/40" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1 }}
            className="md:col-span-5"
          >
            <div className="relative w-full aspect-[4/5] overflow-hidden rounded-2xl bg-[#F5F4F0]">
              <img
                src={product.hero.image}
                alt={product.name}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </Section>

      {/* ---------- Outcomes strip ---------- */}
      <div className="border-y border-[#E8E6E1] bg-[#FDFCFB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-14 grid grid-cols-1 sm:grid-cols-3 gap-8">
          {product.outcomes.map((o) => (
            <div key={o.label}>
              <div className="font-serif-display text-4xl md:text-5xl text-[#1A1A1A] tracking-tight">
                {o.value}
              </div>
              <div className="mt-2 caption !text-[11px]">{o.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ---------- Who it's for ---------- */}
      <Section id="who" bg="bg-[#F5F4F0]">
        <Caption>Who it's for</Caption>
        <H2 className="max-w-3xl">
          Built for teams who can't <span className="italic">afford to wait.</span>
        </H2>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {product.personas.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="p-6 rounded-2xl bg-[#FDFCFB] border border-[#E8E6E1] hover:border-[#1A1A1A]/30 transition-colors"
            >
              <div className="font-mono-tech text-[10px] tracking-[0.18em] text-[#6B6A68] mb-3">
                / {String(i + 1).padStart(2, "0")}
              </div>
              <h3 className="font-serif-display text-xl md:text-2xl text-[#1A1A1A] tracking-tight mb-3">
                {p.name}
              </h3>
              <p className="text-sm text-[#6B6A68] leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ---------- Modules ---------- */}
      <Section id="modules">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <Caption>Modules</Caption>
            <H2 className="max-w-3xl">
              Everything you need. <span className="italic">Nothing you don't.</span>
            </H2>
          </div>
          <p className="max-w-sm text-[#6B6A68] md:text-right">
            Each module is a fully-featured app on its own — together they form
            one calm system.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-5 border-t border-[#E8E6E1]">
            {product.modules.map((m, i) => {
              const active = openModule === i;
              return (
                <button
                  key={m.name}
                  onClick={() => setOpenModule(i)}
                  className={`w-full text-left flex items-baseline gap-4 py-4 border-b border-[#E8E6E1] group transition-colors ${
                    active ? "text-[#1A1A1A]" : "text-[#6B6A68] hover:text-[#1A1A1A]"
                  }`}
                >
                  <span className="font-mono-tech text-[10px] tracking-[0.18em]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-serif-display text-xl md:text-2xl tracking-tight">
                    {m.name}
                  </span>
                  {active && (
                    <span className="ml-auto w-2 h-2 rounded-full bg-[#1A1A1A]" />
                  )}
                </button>
              );
            })}
          </div>
          <div className="md:col-span-7">
            <motion.div
              key={openModule}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
              className="p-8 md:p-10 rounded-2xl bg-[#1A1A1A] text-[#FDFCFB] min-h-[280px] flex flex-col justify-between"
            >
              <div>
                <div className="font-mono-tech text-[10px] tracking-[0.2em] text-[#FDFCFB]/50 mb-4">
                  MODULE / {String(openModule + 1).padStart(2, "0")}
                </div>
                <h3 className="font-serif-display text-3xl md:text-4xl tracking-tight mb-5">
                  {product.modules[openModule].name}
                </h3>
                <p className="text-[#FDFCFB]/75 leading-relaxed">
                  {product.modules[openModule].desc}
                </p>
              </div>
              <div className="mt-6 inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase font-mono-tech text-[#FDFCFB]/60">
                Included on every plan
                <Check className="w-3.5 h-3.5" />
              </div>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* ---------- Ready-to-use packs ---------- */}
      <Section id="packs" bg="bg-[#F5F4F0]">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <Caption>Ready-to-use Packs</Caption>
            <H2 className="max-w-3xl">
              Pick a pack. <span className="italic">Go live today.</span>
            </H2>
          </div>
          <p className="max-w-md text-[#6B6A68] md:text-right">
            Pre-configured solutions for the most common use cases — branding,
            modules and workflows already set up.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {product.packs.map((pk, i) => (
            <motion.div
              key={pk.name}
              data-testid={`pack-${pk.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
              className="group p-6 md:p-7 rounded-2xl bg-[#FDFCFB] border border-[#E8E6E1] hover:border-[#1A1A1A]/30 hover:shadow-[0_22px_40px_-18px_rgba(0,0,0,0.18)] hover:-translate-y-1 transition-all flex flex-col"
            >
              <div className="flex items-start justify-between mb-5">
                <span className="font-mono-tech text-[10px] tracking-[0.18em] text-[#6B6A68]">
                  PACK / {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-mono-tech text-[11px] tracking-[0.12em] text-[#1A1A1A]">
                  {pk.price}
                </span>
              </div>
              <h3 className="font-serif-display text-2xl md:text-[26px] text-[#1A1A1A] tracking-tight mb-2">
                {pk.name}
              </h3>
              <p className="font-serif-display italic text-[#1A1A1A]/75 text-sm md:text-base mb-5">
                {pk.tagline}
              </p>
              <ul className="space-y-2 mb-6 flex-1">
                {pk.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#6B6A68]">
                    <Check className="w-4 h-4 mt-0.5 text-[#1A1A1A]/70 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={onCta}
                data-testid={`pack-cta-${pk.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="mt-auto inline-flex items-center justify-between w-full px-4 py-3 rounded-full border border-[#1A1A1A]/15 text-sm text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FDFCFB] hover:border-[#1A1A1A] transition-all group/btn"
              >
                Use this pack
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
              </button>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ---------- How it works ---------- */}
      <Section id="how">
        <Caption>How it works</Caption>
        <H2 className="max-w-3xl">
          From signup to <span className="italic">live in minutes.</span>
        </H2>
        <div className={`mt-14 grid grid-cols-1 sm:grid-cols-2 ${
          product.howItWorks.length >= 5
            ? "lg:grid-cols-5"
            : product.howItWorks.length === 4
            ? "lg:grid-cols-4"
            : "lg:grid-cols-3"
        } border-t border-l border-[#E8E6E1]`}>
          {product.howItWorks.map((step, i) => (
            <div
              key={step}
              className="relative p-7 border-r border-b border-[#E8E6E1] min-h-[180px] hover:bg-[#F5F4F0] transition-colors"
            >
              <div className="font-mono-tech text-[11px] tracking-[0.2em] text-[#6B6A68] mb-6">
                STEP {String(i + 1).padStart(2, "0")}
              </div>
              <div className="font-serif-display text-xl md:text-2xl text-[#1A1A1A] tracking-tight leading-snug">
                {step}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ---------- Pricing ---------- */}
      <Section id="pricing" bg="bg-[#1A1A1A] text-[#FDFCFB]">
        <Caption>Pricing</Caption>
        <H2 className="!text-[#FDFCFB] max-w-3xl">
          Honest pricing. <span className="italic">No surprises.</span>
        </H2>
        <p className="mt-4 text-[#FDFCFB]/60 max-w-xl text-sm md:text-base">
          Start free, upgrade as you grow. Annual plans get 2 months free. GST
          invoice on every payment.
        </p>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {product.pricing.map((t, i) => (
            <motion.div
              key={t.tier}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className={`relative p-7 rounded-2xl flex flex-col ${
                t.popular
                  ? "bg-[#FDFCFB] text-[#1A1A1A]"
                  : "bg-[#FDFCFB]/[0.04] text-[#FDFCFB] border border-[#FDFCFB]/15"
              }`}
            >
              {t.popular && (
                <div className="absolute -top-3 left-7 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#7EE787] text-[#1A1A1A] text-[10px] tracking-[0.18em] uppercase font-mono-tech">
                  <Star className="w-3 h-3" /> Most popular
                </div>
              )}
              <div className="font-mono-tech text-[10px] tracking-[0.18em] uppercase mb-4 opacity-70">
                {t.tier}
              </div>
              <div className="font-serif-display text-3xl md:text-4xl tracking-tight mb-6">
                {t.price}
              </div>
              <ul className="space-y-2 mb-7 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm opacity-90">
                    <Check className="w-4 h-4 mt-0.5 shrink-0 opacity-80" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={onCta}
                className={`mt-auto inline-flex items-center justify-between gap-2 px-5 py-3 rounded-full text-sm transition-all ${
                  t.popular
                    ? "bg-[#1A1A1A] text-[#FDFCFB] hover:bg-[#333]"
                    : "border border-[#FDFCFB]/30 hover:bg-[#FDFCFB] hover:text-[#1A1A1A]"
                }`}
              >
                {t.tier.toLowerCase().includes("enterprise") ? "Talk to sales" : "Get started"}
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ---------- Integrations + Compliance ---------- */}
      <Section id="trust" bg="bg-[#F5F4F0]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
          <div>
            <Caption>Integrations</Caption>
            <h3 className="font-serif-display text-3xl md:text-4xl text-[#1A1A1A] tracking-tight leading-[1.1] mb-6">
              Plays well with <span className="italic">everything you already use.</span>
            </h3>
            <div className="flex flex-wrap gap-2">
              {product.integrations.map((i) => (
                <Pill key={i}>{i}</Pill>
              ))}
            </div>
          </div>
          <div>
            <Caption>Security & Compliance</Caption>
            <h3 className="font-serif-display text-3xl md:text-4xl text-[#1A1A1A] tracking-tight leading-[1.1] mb-6">
              Built to handle <span className="italic">serious data.</span>
            </h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6">
              {product.compliance.map((c) => (
                <li key={c} className="flex items-start gap-2 text-sm text-[#1A1A1A]">
                  <Check className="w-4 h-4 mt-0.5 text-[#1A1A1A]/70 shrink-0" />
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* ---------- FAQ ---------- */}
      <Section id="faq">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <Caption>FAQ</Caption>
            <H2 className="max-w-md">
              Things people <span className="italic">often ask.</span>
            </H2>
          </div>
          <div className="md:col-span-8 border-t border-[#E8E6E1]">
            {product.faqs.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={f.q} className="border-b border-[#E8E6E1]">
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="w-full text-left flex items-center justify-between gap-6 py-5 group"
                    data-testid={`faq-${i}`}
                  >
                    <span className="font-serif-display text-lg md:text-xl text-[#1A1A1A] tracking-tight">
                      {f.q}
                    </span>
                    <span className="shrink-0 w-9 h-9 rounded-full border border-[#1A1A1A]/20 flex items-center justify-center group-hover:bg-[#1A1A1A] group-hover:text-[#FDFCFB] transition-colors">
                      {isOpen ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                    </span>
                  </button>
                  {isOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      transition={{ duration: 0.35 }}
                      className="pb-6 text-[#6B6A68] leading-relaxed text-[15px] max-w-2xl"
                    >
                      {f.a}
                    </motion.div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ---------- Final CTA ---------- */}
      <Section id="final-cta" bg="bg-[#1A1A1A] text-[#FDFCFB]">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <Caption>Ready when you are</Caption>
            <h2 className="font-serif-display text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight">
              Start with {product.brand || product.name} <span className="italic">— today.</span>
            </h2>
            <p className="mt-5 text-[#FDFCFB]/65 max-w-xl text-base md:text-lg leading-relaxed">
              Free to start, no card required. Talk to our team if you want
              help picking a pack or migrating from existing software.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3">
            <button
              onClick={onCta}
              className="group inline-flex items-center justify-between gap-2 px-6 py-4 rounded-full bg-[#FDFCFB] text-[#1A1A1A] text-sm font-medium hover:bg-[#FDFCFB]/90 transition-all"
            >
              Start Free
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              onClick={onCta}
              className="group inline-flex items-center justify-between gap-2 px-6 py-4 rounded-full border border-[#FDFCFB]/30 text-[#FDFCFB] text-sm hover:bg-[#FDFCFB] hover:text-[#1A1A1A] transition-all"
            >
              Book a Demo
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>
        </div>
      </Section>

      {/* ---------- Related ---------- */}
      {product.related?.length > 0 && (
        <Section id="related" bg="bg-[#F5F4F0]">
          <Caption>Related products</Caption>
          <H2 className="max-w-3xl">
            More from the <span className="italic">Heritex platform.</span>
          </H2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {product.related.map((rs) => {
              const r = productsBySlug[rs];
              if (!r) return null;
              return (
                <Link
                  key={rs}
                  to={`/products/${rs}`}
                  data-testid={`related-${rs}`}
                  className="group p-6 rounded-2xl bg-[#FDFCFB] border border-[#E8E6E1] hover:border-[#1A1A1A]/30 hover:-translate-y-1 hover:shadow-[0_22px_40px_-18px_rgba(0,0,0,0.18)] transition-all flex flex-col min-h-[200px]"
                >
                  <div className="font-mono-tech text-[10px] tracking-[0.18em] text-[#6B6A68] mb-4">
                    {r.category}
                  </div>
                  <h3 className="font-serif-display text-2xl text-[#1A1A1A] tracking-tight mb-3">
                    {r.name}
                  </h3>
                  <p className="text-sm text-[#6B6A68] leading-relaxed flex-1">
                    {r.tagline}
                  </p>
                  <div className="mt-5 inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase font-mono-tech text-[#1A1A1A]/70">
                    Explore
                    <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </Link>
              );
            })}
          </div>
        </Section>
      )}

      <Footer />
      <StickyCta />
    </main>
  );
}
